import { type ReactNode, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useScrollDepth } from '../../../hooks/useScrollDepth'
import { track } from '../../../utils/analytics'
import type { ConsentFlowSteps } from '../../../utils/consentFlowHelper'
import {
  ConsentFlowCard,
  OPTIONAL_INTRO_IMAGE,
  PrimaryAction,
  RESEARCH_PARTICIPATION_IMAGE,
  StepPill,
  WorkflowProgress,
} from './ConsentFlowLayout'
import { getSecondaryTitle } from './consentOptionsUtils'
import type { ConsentConfigItem } from './types'

export function SecondaryIntroScreen({
  hasItems,
  workflowSteps,
}: {
  hasItems: boolean
  workflowSteps: ConsentFlowSteps
}) {
  const intl = useIntl()
  const navigate = useNavigate()

  return (
    <ConsentFlowCard
      imageSrc={OPTIONAL_INTRO_IMAGE}
      progress={<WorkflowProgress current={[workflowSteps.secondary]} next={[workflowSteps.profile]} />}
    >
      <div className="space-y-6">
        <StepPill>{intl.formatMessage({ id: workflowSteps.secondary.labelKey })}</StepPill>
        <h1 className="text-3xl font-semibold tracking-tight text-[#002C22] sm:text-4xl">
          <FormattedMessage
            id="consentOptions.secondaryIntro.heading"
            defaultMessage="A few choices that could help your family, and others - now and in the future."
          />
        </h1>
        <p className="text-text-body text-base leading-7">
          <FormattedMessage
            id="consentOptions.secondaryIntro.body1"
            defaultMessage="The choices on the following screens will not affect your child's care. But they could help advance medical research."
          />
        </p>
        <p className="text-text-body text-base leading-7">
          <FormattedMessage
            id="consentOptions.secondaryIntro.body2"
            defaultMessage="We'll walk you through four questions so you can choose how Inflection cares for your family's information. You'll decide whether to contribute to internal research that improves our testing, whether to allow sample retention for future use, whether to share anonymized data with trusted research partners, and whether to connect your health records to help us better interpret your results."
          />
        </p>
        <div className="border-border-base bg-background-secondary rounded-xl border px-4 py-3">
          <p className="text-sm leading-6 font-medium text-[#006045]">
            <FormattedMessage
              id="consentOptions.secondaryIntro.infoBox"
              defaultMessage="Every choice is yours and you can change your answers at any time by visiting your profile."
            />
          </p>
        </div>
        <p className="text-text-subtle text-sm leading-6">
          <FormattedMessage
            id="consentOptions.secondaryIntro.hint"
            defaultMessage="Next, you'll see each option with a plain-language explanation and a chance to Accept or Decline."
          />
        </p>
        <p className="text-text-subtle text-sm leading-6">
          <FormattedMessage
            id="consentOptions.secondaryIntro.repScope"
            defaultMessage={
              'For the purposes of this consent, "you", "me", "my", and "I" can also mean your child ' +
              '(including unborn child) or the individual you are the personal representative for.'
            }
          />
        </p>
        <PrimaryAction onClick={() => navigate(hasItems ? '/consents/secondary/2' : '/profile')}>
          <FormattedMessage id="consentOptions.cta.continue" defaultMessage="Continue" />
        </PrimaryAction>
      </div>
    </ConsentFlowCard>
  )
}

export function SecondaryChoiceScreen({
  item,
  value,
  valueSelf,
  valueDependent,
  dualSecondary,
  dependentSubtitle,
  saving,
  onSelect,
  onSelectSelf,
  onSelectDependent,
  onContinue,
  workflowSteps,
  step,
}: {
  item: ConsentConfigItem
  /** Single-subject mode (legacy): one choice for optional secondary. */
  value?: boolean | undefined
  onSelect?: (value: boolean) => void
  /** When ``dualSecondary``, choices for signer vs dependent (item id suffix in parent state). */
  valueSelf?: boolean | undefined
  valueDependent?: boolean | undefined
  onSelectSelf?: (value: boolean) => void
  onSelectDependent?: (value: boolean) => void
  dualSecondary?: boolean
  dependentSubtitle?: string
  saving: boolean
  onContinue: () => void
  workflowSteps: ConsentFlowSteps
  /** Step index from the URL segment — passed to analytics payload. */
  step?: number
}) {
  const selfReady = dualSecondary ? valueSelf !== undefined : value !== undefined
  const depReady = dualSecondary ? valueDependent !== undefined : true
  const canContinue = selfReady && depReady

  const [isOpen, setIsOpen] = useState(false)
  const learnMoreParagraphs = item.descriptionParagraphs?.slice(1) ?? []
  const hasLearnMore = learnMoreParagraphs.length > 0

  const intl = useIntl()
  const trackDescScroll = useScrollDepth(`secondary_consent_text_${item.id}`)
  const trackExpandedDescScroll = useScrollDepth(`secondary_consent_expanded_text_${item.id}`)

  useEffect(() => {
    setIsOpen(false)
  }, [item.id])

  function handleDisclosureToggle(next: boolean) {
    setIsOpen(next)
    track(next ? 'consent.section_expanded' : 'consent.section_collapsed', {
      item_id: item.id,
      step,
    })
  }

  return (
    <ConsentFlowCard
      imageSrc={RESEARCH_PARTICIPATION_IMAGE}
      progress={<WorkflowProgress current={[workflowSteps.secondary]} next={[workflowSteps.profile]} />}
    >
      <div className="space-y-8">
        <StepPill>{intl.formatMessage({ id: workflowSteps.secondary.labelKey })}</StepPill>
        <div className="space-y-5">
          <h1 className="text-3xl font-semibold tracking-tight text-[#002C22] sm:text-4xl">
            {getSecondaryTitle(item)}
          </h1>
          <div className="space-y-4">
            <div ref={trackDescScroll} className="max-h-56 overflow-y-auto pr-2">
              <p className="text-text-body text-base leading-7">
                {item.descriptionParagraphs?.[0] ?? item.description ?? item.title}
              </p>
            </div>
            {hasLearnMore && (
              <>
                {!isOpen ? (
                  <button
                    type="button"
                    aria-expanded="false"
                    onClick={() => handleDisclosureToggle(true)}
                    className="text-sm font-medium text-[#006045] underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#006045]"
                  >
                    {intl.formatMessage({
                      id: 'consentOptions.secondaryChoice.learnMore',
                      defaultMessage: 'Learn more',
                    })}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div
                      ref={trackExpandedDescScroll}
                      id={`learn-more-${item.id}`}
                      role="region"
                      aria-label={`${getSecondaryTitle(item)} details`}
                      className="max-h-48 space-y-3 overflow-y-auto overscroll-contain rounded-lg border border-[#D0D5DD] bg-[#F8FAF9] px-4 py-3 pr-3"
                    >
                      {learnMoreParagraphs.map((p, i) => (
                        <p key={i} className="text-text-body text-base leading-7">
                          {p}
                        </p>
                      ))}
                    </div>
                    <button
                      type="button"
                      aria-expanded="true"
                      aria-controls={`learn-more-${item.id}`}
                      onClick={() => handleDisclosureToggle(false)}
                      className="text-sm font-medium text-[#006045] underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#006045]"
                    >
                      {intl.formatMessage({
                        id: 'consentOptions.secondaryChoice.showLess',
                        defaultMessage: 'Show less',
                      })}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {dualSecondary ? (
          <>
            <fieldset className="grid gap-3 border-0 p-0 sm:grid-cols-2">
              <legend className="col-span-full mb-1 text-sm font-semibold text-[#002C22]">
                <FormattedMessage id="consentOptions.secondaryChoice.forYou" defaultMessage="For you" />
              </legend>
              <SecondaryChoiceLabel
                label={<FormattedMessage id="consentOptions.secondaryChoice.accept" defaultMessage="Accept" />}
                checked={valueSelf === true}
                onSelect={() => onSelectSelf?.(true)}
              />
              <SecondaryChoiceLabel
                label={<FormattedMessage id="consentOptions.secondaryChoice.decline" defaultMessage="Decline" />}
                checked={valueSelf === false}
                onSelect={() => onSelectSelf?.(false)}
              />
            </fieldset>
            <fieldset className="grid gap-3 border-0 p-0 sm:grid-cols-2">
              <legend className="col-span-full mb-1 text-sm font-semibold text-[#002C22]">
                {dependentSubtitle ?? (
                  <FormattedMessage id="consentOptions.secondaryChoice.forYourChild" defaultMessage="For your child" />
                )}
              </legend>
              <SecondaryChoiceLabel
                label={<FormattedMessage id="consentOptions.secondaryChoice.accept" defaultMessage="Accept" />}
                checked={valueDependent === true}
                onSelect={() => onSelectDependent?.(true)}
              />
              <SecondaryChoiceLabel
                label={<FormattedMessage id="consentOptions.secondaryChoice.decline" defaultMessage="Decline" />}
                checked={valueDependent === false}
                onSelect={() => onSelectDependent?.(false)}
              />
            </fieldset>
          </>
        ) : (
          <fieldset className="grid gap-3 border-0 p-0 sm:grid-cols-2">
            <legend className="sr-only">
              <FormattedMessage
                id="consentOptions.secondaryChoice.srLegend"
                defaultMessage="Accept or decline {title}"
                values={{ title: getSecondaryTitle(item) }}
              />
            </legend>
            <SecondaryChoiceLabel
              label={<FormattedMessage id="consentOptions.secondaryChoice.accept" defaultMessage="Accept" />}
              checked={value === true}
              onSelect={() => onSelect?.(true)}
            />
            <SecondaryChoiceLabel
              label={<FormattedMessage id="consentOptions.secondaryChoice.decline" defaultMessage="Decline" />}
              checked={value === false}
              onSelect={() => onSelect?.(false)}
            />
          </fieldset>
        )}
        <p className="text-text-subtle text-sm leading-6">
          <FormattedMessage
            id="consentOptions.secondaryChoice.hint"
            defaultMessage="This doesn't affect your child's care. You can change this at any time from your profile."
          />
        </p>
        <PrimaryAction disabled={!canContinue || saving} onClick={onContinue}>
          {saving ? (
            <FormattedMessage id="consentOptions.consentSign.saving" defaultMessage="Saving" />
          ) : (
            <FormattedMessage id="consentOptions.cta.continue" defaultMessage="Continue" />
          )}
        </PrimaryAction>
      </div>
    </ConsentFlowCard>
  )
}

function SecondaryChoiceLabel({
  label,
  checked,
  onSelect,
}: {
  label: ReactNode
  checked: boolean
  onSelect: () => void
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-full border px-4 py-3 text-sm font-semibold transition ${
        checked ? 'border-[#006045] text-[#002C22]' : 'border-border-base text-text-body'
      }`}
    >
      <input type="radio" checked={checked} onChange={onSelect} className="h-4 w-4 accent-[#006045]" />
      {label}
    </label>
  )
}
